/**
 * SubmissionDetails — "What the client told us".
 *
 * One dedicated card on every lead record that surfaces the client's OWN
 * submission off the website / free-tool form, adapting to the lead's source.
 * This restores the detail the legacy LeadDetail/E2ScoreBlock screen showed
 * but the new engagement-flow Leads screen had dropped.
 *
 * Reads `lead.submission` (populated by leadStore.flatDocToLead via
 * lib/leadSubmission.extractSubmission). Renders nothing when there is no
 * submission payload (portal-native / WhatsApp leads). Every row is guarded —
 * an absent field is never shown as an empty row.
 *
 * Styling: the live portal UI system — white canvas (Box/Card from ui.tsx),
 * design tokens only, no new UI variant. Reuses the legacy humanize maps
 * (ported into lib/leadSubmission) and the level→token colour mapping.
 *
 * Any HTML-bearing string (No-Go reasons) is sanitized with DOMPurify before
 * render; everything else is plain text.
 *
 * 2026-06-21 — Anil.
 */
import DOMPurify from 'dompurify';
import { Box, Card, Tag } from './ui';
import {
  humanizeLead,
  LEVEL_TOKEN,
  type LeadSubmission,
  type SubmissionObservation,
} from '../lib/leadSubmission';
import type { Lead } from '../lib/leads';

/* ── tiny presentational atoms (tokens only) ── */

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

/** A key/value row — renders nothing if the value is empty. */
function KV({ k, v }: { k: string; v?: string | null }) {
  if (!v) return null;
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginBottom: 2 }}>
        {k}
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--ink)', wordBreak: 'break-word' }}>
        {v}
      </div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

/** One observation / flag row with the severity wash + label. */
function ObsRow({ obs }: { obs: SubmissionObservation }) {
  const tok = obs.level ? LEVEL_TOKEN[obs.level] : null;
  const fg = tok ? tok.fg : 'var(--ink-mute)';
  const wash = tok ? tok.wash : 'var(--inset)';
  const label = tok ? tok.label : 'info';
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '8px 10px',
        background: wash,
        borderRadius: 'var(--r-sm)',
        borderLeft: `3px solid ${fg}`,
      }}
    >
      <span
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 700,
          color: fg,
          minWidth: 58,
        }}
      >
        {label}
      </span>
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.45, color: 'var(--ink)' }}>
        {obs.text}
      </div>
    </div>
  );
}

function ObsList({ items }: { items: SubmissionObservation[] }) {
  // Order red → amber → green → unlabelled (most actionable first).
  const order = { red: 0, amber: 1, green: 2 } as const;
  const sorted = [...items].sort(
    (a, b) =>
      (a.level ? order[a.level] : 3) - (b.level ? order[b.level] : 3),
  );
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {sorted.map((o, i) => (
        <ObsRow key={i} obs={o} />
      ))}
    </div>
  );
}

/* ── score ring (E2/E4) ── */

function scoreBand(score: number): { label: string; level: 'green' | 'amber' | 'red' } {
  if (score >= 80) return { label: 'Strong alignment', level: 'green' };
  if (score >= 50) return { label: 'A few items to look at', level: 'amber' };
  return { label: 'Significant concerns', level: 'red' };
}

function ScoreRing({ score }: { score: number }) {
  const band = scoreBand(score);
  const fg = LEVEL_TOKEN[band.level].fg;
  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: 96,
          height: 96,
          borderRadius: '50%',
          border: `5px solid ${fg}`,
          background: 'var(--surface)',
          flexShrink: 0,
        }}
      >
        <div style={{ fontSize: 32, fontWeight: 700, lineHeight: 1, color: fg }}>
          {score}
        </div>
        <div style={{ fontSize: 9, color: 'var(--ink-mute)', letterSpacing: '0.08em', marginTop: 3 }}>
          OUT OF 100
        </div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: fg }}>{band.label}</div>
    </div>
  );
}

/* ── header strip — always shown ── */

function HeaderStrip({ lead, sub }: { lead: Lead; sub: LeadSubmission }) {
  const when = sub.submittedAt
    ? new Date(sub.submittedAt).toLocaleString()
    : '';
  const where = [sub.sourcePage, sub.sourceLabel || sub.rawSource]
    .filter(Boolean)
    .join(' · ');
  return (
    <Card flat>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <b style={{ fontSize: 14, color: 'var(--ink)' }}>{lead.name}</b>
        {lead.phone && <Tag kind="tag">{lead.phone}</Tag>}
        {lead.email && <Tag kind="tag">{lead.email}</Tag>}
        {sub.country && <Tag>{sub.country}</Tag>}
      </div>
      {(where || when) && (
        <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 6 }}>
          {where}
          {where && when ? ' · ' : ''}
          {when}
        </div>
      )}
    </Card>
  );
}

/* ════════════════════ the component ════════════════════ */

export default function SubmissionDetails({ lead }: { lead: Lead }) {
  const sub = lead.submission;
  if (!sub) return null;

  const src = sub.rawSource;
  const isProperty = src === 'E2' || src === 'E4';

  return (
    <Box
      title="What the client told us"
      note={`${sub.sourceLabel || src || 'Website form'} · the submitted details`}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <HeaderStrip lead={lead} sub={sub} />

        {/* ── contact / service_enquiry ── */}
        {(src === 'contact' || src === 'service_enquiry') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Grid>
              <KV
                k={src === 'contact' ? 'They want help with' : 'Service wanted'}
                v={sub.serviceWanted}
              />
              {src === 'service_enquiry' && (
                <KV k="Property type" v={humanizeLead.propertyType(sub.propertyTypeRaw || null)} />
              )}
              <KV k="City" v={sub.city} />
            </Grid>
            {sub.message && (
              <div>
                <Lbl>Their message</Lbl>
                <Card flat>
                  <div style={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {sub.message}
                  </div>
                </Card>
              </div>
            )}
            {typeof sub.consentMarketing === 'boolean' && (
              <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                Marketing consent: {sub.consentMarketing ? 'yes' : 'no'}
              </div>
            )}
          </div>
        )}

        {/* ── guide leads (E1 / E3) ── */}
        {(src === 'E1' || src === 'E3') && (
          <Grid>
            <KV k="Guide requested" v={sub.guideRequested} />
            {typeof sub.consentMarketing === 'boolean' && (
              <KV k="Marketing consent" v={sub.consentMarketing ? 'yes' : 'no'} />
            )}
          </Grid>
        )}

        {/* ── property-score (E2) + home-assessment (E4) ── */}
        {isProperty && (
          <>
            {typeof sub.score === 'number' && (
              <div>
                <Lbl>Property score</Lbl>
                <ScoreRing score={sub.score} />
              </div>
            )}

            {/* E4 area scoreboard */}
            {sub.areas && sub.areas.length > 0 && (
              <div>
                <Lbl>Life areas (their concern lens)</Lbl>
                <Grid>
                  {sub.areas.map((a) => {
                    const tok = a.level ? LEVEL_TOKEN[a.level] : null;
                    const fg = tok ? tok.fg : 'var(--ink)';
                    return (
                      <Card key={a.key} flat>
                        <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginBottom: 2 }}>
                          {a.label}
                        </div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: fg }}>
                          {a.band || (a.score != null ? `${a.score}/100` : '—')}
                        </div>
                      </Card>
                    );
                  })}
                </Grid>
              </div>
            )}

            {/* property answers */}
            <div>
              <Lbl>Property answers</Lbl>
              <Grid>
                <KV k="Type" v={emptyDash(humanizeLead.propertyType(sub.propertyTypeRaw || null))} />
                <KV k="Status" v={emptyDash(humanizeLead.propertyStatus(sub.propertyStatusRaw || null))} />
                <KV k="Shape" v={emptyDash(humanizeLead.shape(sub.propertyShapeRaw || null))} />
                <KV k="Slope" v={emptyDash(humanizeLead.slope(sub.propertySlopeRaw || null))} />
                <KV k="Entry facing" v={emptyDash(humanizeLead.entryFacing(sub.entryFacingRaw || null))} />
                <KV k="Entry codes" v={humanizeLead.codeList(sub.entryCodes) || undefined} />
                <KV k="Primary concern" v={humanizeLead.primaryConcern(sub.primaryConcernRaw || null) || undefined} />
              </Grid>
            </div>

            {/* room-by-room zones */}
            {sub.rooms && sub.rooms.length > 0 && (
              <div>
                <Lbl>Rooms &amp; zones</Lbl>
                <div style={{ display: 'grid', gap: 6 }}>
                  {sub.rooms.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '7px 11px',
                        background: 'var(--inset)',
                        borderRadius: 'var(--r-sm)',
                        fontSize: 13,
                      }}
                    >
                      <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{r.label}</span>
                      <span style={{ color: 'var(--ink-soft)' }}>{r.zone}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* observations (E2) */}
            {sub.observations && sub.observations.length > 0 && (
              <div>
                <Lbl>Observations ({sub.observations.length})</Lbl>
                <ObsList items={sub.observations} />
              </div>
            )}

            {/* flags (E4) */}
            {sub.flags && sub.flags.length > 0 && (
              <div>
                <Lbl>Flags ({sub.flags.length})</Lbl>
                <ObsList items={sub.flags} />
              </div>
            )}
          </>
        )}

        {/* ── uploaded map (E2/E4) — shown for any source that carries one ── */}
        {sub.mapUrl && (
          <div>
            <Lbl>Uploaded map / floor plan</Lbl>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
              <a
                href={sub.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="avbp-lnk"
              >
                {sub.mapFilename || 'View uploaded map'} ↗
              </a>
              {sub.mapNorth && (
                <Tag kind="tag">North: {sub.mapNorth}</Tag>
              )}
              {sub.gmapLink && (
                <a href={sub.gmapLink} target="_blank" rel="noopener noreferrer" className="avbp-lnk">
                  Google Maps ↗
                </a>
              )}
            </div>
          </div>
        )}

        {/* ── No-Go soft-fail reasons (HTML-bearing → sanitized) ── */}
        {sub.nogoReasons && sub.nogoReasons.length > 0 && (
          <div>
            <Lbl>Flagged for a closer look</Lbl>
            <div
              style={{
                padding: '8px 10px',
                background: 'var(--warn-wash)',
                borderRadius: 'var(--r-sm)',
                borderLeft: '3px solid var(--warn)',
              }}
            >
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: 'var(--ink)' }}>
                {sub.nogoReasons.map((r, i) => (
                  <li
                    key={i}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(r, {
                        ALLOWED_TAGS: ['strong', 'b', 'em', 'i'],
                        ALLOWED_ATTR: [],
                      }),
                    }}
                  />
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </Box>
  );
}

/** Treat the humanize "—" fallback as absent so the KV row is hidden. */
function emptyDash(s: string): string | undefined {
  return !s || s === '—' ? undefined : s;
}
